/**
 * Business email validation utilities.
 */

const INVALID_EMAIL_DOMAINS = [
    '@outlook.',
    '@gmail.',
    '@googlemail.',
    '@yahoo.',
    '@qq.',
    '@yandex.',
    '@inbox.',
    '@aol.',
    '@me.',
    '@mac.',
    '@icloud.',
    '@mail.com',
    '@mailinator.com',
    '@hotmail.',
    '@protonmail.',
    '@live.',
    '@yevme.',
    '@inpwa.',
    '@fexpost.',
    '@mailto.',
    '@fexbox.',
    '@mailbox.',
    '@rover.',
    '@intopwa.',
    '@web.de',
    '@wp.pl',
] as const;

/**
 * Returns the hostname part of an email (after the last "@"), lowercased, or null if missing.
 */
function getEmailDomain(email: string): string | null {
    const at = email.lastIndexOf('@');
    return email
        .slice(at + 1)
        .toLowerCase()
        .trim();
}

/**
 * True when the email's domain is a blocked consumer domain.
 * Uses the parsed hostname so blocked tokens cannot match as substrings (e.g. @web.de vs web.dev).
 */
function isBlockedConsumerDomain(domain: string): boolean {
    return INVALID_EMAIL_DOMAINS.some((pattern) => {
        const p = pattern.slice(1).toLowerCase();
        if (p.endsWith('.')) {
            return domain.startsWith(p) || domain.includes(`.${p}`);
        }
        return domain === p || domain.endsWith(`.${p}`);
    });
}

// RFC-ish email format validation
const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/** Returns true when the email passes RFC-ish format validation. */
export function isValidEmail(email: string): boolean {
    return EMAIL_REGEX.test(email);
}

/**
 * Returns true when the address has a parseable host and that host is not a known personal/free domain.
 * Missing or empty hosts (e.g. no "@", trailing "@") are not treated as business email.
 */
export function isBusinessEmail(validEmail: string): boolean {
    if (!isValidEmail(validEmail)) {
        return false;
    }
    const domain = getEmailDomain(validEmail);
    if (!domain) {
        return false;
    }
    return !isBlockedConsumerDomain(domain);
}
